import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Check, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/common/components/dialog';

import {
  useCreateApiKeyMutation,
  ApiKeyCreateResponse,
} from '@common-reducers/backend-api/backend-api';
import { namespace } from '@common/config/constants';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

import { ApiKeyAccess, scopesForAccess } from './api-key-access';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
}

export function CreateApiKeyDialog({ open, onOpenChange, workspaceId }: Props) {
  const { t } = useTranslation(namespace);
  const [name, setName] = useState('');
  const [created, setCreated] = useState<ApiKeyCreateResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const [access, setAccess] = useState<ApiKeyAccess>('full');
  const [createApiKey, { isLoading }] = useCreateApiKeyMutation();

  const reset = () => {
    setName('');
    setAccess('full');
    setCreated(null);
    setCopied(false);
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      const res = await createApiKey({
        workspaceId,
        name: trimmed,
        scopes: scopesForAccess(access),
      }).unwrap();
      setCreated(res);
    } catch {
      toast.error(t('api.keys.createDialog.error'));
    }
  };

  const copy = async () => {
    if (!created?.fullKey) return;
    await navigator.clipboard.writeText(created.fullKey);
    setCopied(true);
    toast.success(t('api.copySuccess'));
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-md">
        {!created ? (
          <>
            <DialogHeader>
              <DialogTitle>{t('api.keys.createDialog.title')}</DialogTitle>
              <DialogDescription>
                {t('api.keys.createDialog.nameHelp')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-4">
              <Label htmlFor="api-key-name">
                {t('api.keys.createDialog.nameLabel')}
              </Label>
              <Input
                id="api-key-name"
                value={name}
                autoFocus
                maxLength={80}
                placeholder={t('api.keys.createDialog.namePlaceholder')}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && name.trim()) submit();
                }}
              />
            </div>
            <div className="space-y-2 pb-4">
              <Label>{t('api.keys.createDialog.accessLabel')}</Label>
              <RadioGroup
                value={access}
                onValueChange={(v) => setAccess(v === 'read' ? 'read' : 'full')}
                aria-label={t('api.keys.createDialog.accessLabel')}
              >
                {(['full', 'read'] as const).map((level) => (
                  <label
                    key={level}
                    htmlFor={`api-key-access-${level}`}
                    className="flex cursor-pointer items-start gap-3 rounded-md border border-[var(--color-border)] p-3 text-sm"
                  >
                    <RadioGroupItem
                      id={`api-key-access-${level}`}
                      value={level}
                      className="mt-0.5"
                    />
                    <span>
                      <span className="block font-medium text-[var(--color-text)]">
                        {t(`api.keys.createDialog.access.${level}.title`)}
                      </span>
                      <span className="block text-xs text-[var(--color-text-muted)]">
                        {t(`api.keys.createDialog.access.${level}.help`)}
                      </span>
                    </span>
                  </label>
                ))}
              </RadioGroup>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={close} disabled={isLoading}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={submit}
                disabled={isLoading || !name.trim()}
                className="bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent)]/90 border-transparent"
              >
                {t('api.keys.createDialog.submit')}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>
                {t('api.keys.createDialog.successTitle')}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{t('api.keys.createDialog.successWarning')}</span>
              </div>
              <div className="flex items-end gap-2">
                <Input
                  readOnly
                  value={created.fullKey}
                  className="flex-1 font-mono text-xs"
                />
                <Button onClick={copy} variant="outline" className="gap-2 shrink-0">
                  {copied ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                  {copied
                    ? t('api.keys.createDialog.copied')
                    : t('api.keys.createDialog.copyButton')}
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button
                onClick={close}
                className="bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent)]/90 border-transparent"
              >
                {t('api.keys.createDialog.done')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
